import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, useIntl } from 'react-intl';
import { Button } from 'ui-components/components/button';
import { LoadingIndicator } from 'ui-components/components/loading-indicator';
import { MDash } from 'ui-components/components/mdash';
import { Modal } from 'ui-components/components/modal';
import { ModalBody } from 'ui-components/components/modal/modal-body';
import { ModalFooter } from 'ui-components/components/modal/modal-footer';
import { ModalFooterCancelButton } from 'ui-components/components/modal/modal-footer/cancel-button';
import { ModalHeader } from 'ui-components/components/modal/modal-header';
import { RadioCheckList } from 'ui-components/components/radio-check-list';
import { RadioCheckListItem } from 'ui-components/components/radio-check-list/radio-check-list-item';
import { withGuard } from 'ui-components/components/with-guard';
import { useSellerWallets } from '../../../../../../hooks/use-seller-wallets';
import ModalPortal from '../../../../../../components/modal-portal';
import { ErrorModal } from '../../../../../../components/modals/error-modal';
import { useSellerFormattedDate } from '../../../../../../hooks/use-datetime-formats';
import { applyCarrierSharingDefaults, getCarrierSharing, saveCarrierSharing } from '../../../api';
import { handleError } from '../../../carrier-sharing-utils';
import { useProvidersGridContext } from '../../../providers-grid-context';
import { PendingProviderPropType, ProviderPropType } from '../../../types';
import { ServicesTreeDataWrapper } from '../services-tree-data-wrapper';
import { WalletWarningModal } from '../wallet-warning-modal/wallet-warning-modal';
import messages from './messages';
import styles from './styles.module.less';
import { validateServicesAgainstNotRegisteredWallet, validateServicesSelectedFromDifferentIntegrations } from './carrier-sharing-utils';

export const CustomCarrierSharingModal = withGuard(({ provider, onClose, refreshGridData }) => {
  const { carrierSharing, labelProviders, carriers, activeServices } = useProvidersGridContext();

  const name = provider.partnerName;
  const isCarrierSharingDefault = provider.carrierSharing?.isDefault;
  const externalClientConnectionId = provider.externalClientConnectionId;
  const formattedModificationDate = useSellerFormattedDate(provider.carrierSharing?.modificationDate);
  const modificationDate = !isCarrierSharingDefault && provider.carrierSharing?.modificationDate ? formattedModificationDate : <MDash />;

  const { formatMessage } = useIntl();
  const wallets = useSellerWallets();

  const [isPartnerDefault, setIsPartnerDefault] = useState(isCarrierSharingDefault);
  const [carrierSharingCustomApiDataLoaded, setCarrierSharingCustomApiDataLoaded] = useState(false);

  const [currentCarrierSharingData, setCurrentCarrierSharingData] = useState({});
  const [currentCarrierSharingDataLoaded, setCurrentCarrierSharingDataLoaded] = useState(false);
  const [saveInProgress, setSaveInProgress] = useState(false);
  const [carrierSharingCustom, setCarrierSharingCustom] = useState({});
  const [showValidationWarning, setShowValidationWarning] = useState(false);
  const [showWalletValidationWarning, setShowWalletValidationWarning] = useState(false);
  const [dataAvailable, setDataAvailable] = useState(false);

  const saveButtonActive = useMemo(() => {
    if (!isPartnerDefault) {
      return true;
    }

    // check the initial provider state
    if (provider.carrierSharingDefault) {
      return false;
    }

    return true;
  }, [isPartnerDefault, provider.carrierSharingDefault]);

  const onCheckedDefault = useCallback(() => {
    const newIsPartnerDefault = !isPartnerDefault;

    if (newIsPartnerDefault) {
      setCurrentCarrierSharingData(carrierSharing.data);
    } else {
      setCurrentCarrierSharingData(carrierSharingCustom);
    }

    setCurrentCarrierSharingDataLoaded(false);
    setIsPartnerDefault(newIsPartnerDefault);
  }, [carrierSharingCustom, carrierSharing, isPartnerDefault, setCurrentCarrierSharingData, setCurrentCarrierSharingDataLoaded]);

  const onApplyButtonClick = useCallback(() => {
    const isValidated = validateServicesSelectedFromDifferentIntegrations(currentCarrierSharingData);
    setShowValidationWarning(!isValidated);

    const isWalletValidated = validateServicesAgainstNotRegisteredWallet(currentCarrierSharingData, wallets);
    setShowWalletValidationWarning(!isWalletValidated);

    if (!isValidated || !isWalletValidated) {
      return;
    }

    setSaveInProgress(true);
    const externalIds = [externalClientConnectionId];

    if (isPartnerDefault) {
      applyCarrierSharingDefaults({
        clientExternalConnectionIds: externalIds,
      })
        .then(() => {
          refreshGridData(externalClientConnectionId);
          setSaveInProgress(true);
          onClose();
        })
        .catch(() => {
          handleError(formatMessage(messages.applyDefaultErrorTitle), formatMessage(messages.applyDefaultError));
          setSaveInProgress(false);
        });
    } else {
      const saveCarrierSharingRequest = {
        ...currentCarrierSharingData,
        clientExternalConnectionIds: externalIds,
        isRequired: currentCarrierSharingData.isRequired && !(currentCarrierSharingData.selectedServices.length === 0),
      };

      saveCarrierSharing(saveCarrierSharingRequest)
        .then(() => {
          setSaveInProgress(true);
          refreshGridData(externalClientConnectionId);
          onClose();
        })
        .catch(() => {
          handleError(formatMessage(messages.saveCarrierSharingErrorTitle), formatMessage(messages.saveCarrierSharingError));
          setSaveInProgress(false);
        });
    }
  }, [currentCarrierSharingData, externalClientConnectionId, formatMessage, isPartnerDefault, onClose, refreshGridData, wallets]);

  const onCloseValidationWarning = useCallback(() => {
    setShowValidationWarning(false);
  }, []);

  const onCloseWalletValidationWarning = useCallback(() => {
    setShowWalletValidationWarning(false);
  }, []);

  const modalFrameworkMethods = useMemo(
    () => ({
      closeModal: onCloseValidationWarning,
    }),
    [onCloseValidationWarning],
  );

  const showSpinner = useMemo(() => {
    return carrierSharing.isLoading || !currentCarrierSharingDataLoaded || saveInProgress;
  }, [carrierSharing.isLoading, currentCarrierSharingDataLoaded, saveInProgress]);

  useEffect(() => {
    // data not loaded, skip
    if (currentCarrierSharingDataLoaded || carrierSharing.isLoading) {
      return;
    }

    // if data are loaded, but default is set, copy default configuration to current configuration
    if (isPartnerDefault) {
      setCurrentCarrierSharingData(carrierSharing.data);
      setCurrentCarrierSharingDataLoaded(true);
      return;
    }

    // if default is not set, fetch custom configuration from API, but do it only once
    if (carrierSharingCustomApiDataLoaded) {
      setCurrentCarrierSharingDataLoaded(true);
      return;
    }

    // check the initial provider state
    if (isCarrierSharingDefault) {
      // copy from default
      setCarrierSharingCustom(carrierSharing.data);
      setCurrentCarrierSharingData(carrierSharing.data);

      setCurrentCarrierSharingDataLoaded(true);
      setCarrierSharingCustomApiDataLoaded(true);
    } else {
      // fetch from API
      getCarrierSharing(externalClientConnectionId)
        .then((response) => {
          setCarrierSharingCustom(response.data);
          setCurrentCarrierSharingData(response.data);

          setCurrentCarrierSharingDataLoaded(true);
          setCarrierSharingCustomApiDataLoaded(true);
        })
        .catch(() =>
          handleError(formatMessage(messages.carrierSharingCustomFetchErrorTitle), formatMessage(messages.carrierSharingCustomFetchError)),
        );
    }
  }, [
    carrierSharingCustomApiDataLoaded,
    carrierSharing,
    carrierSharing.isLoading,
    currentCarrierSharingDataLoaded,
    isPartnerDefault,
    provider.carrierSharingDefault,
    provider.externalClientConnectionId,
    isCarrierSharingDefault,
    externalClientConnectionId,
    formatMessage,
  ]);

  return (
    <>
      {showValidationWarning && (
        <ModalPortal onClose={onCloseValidationWarning}>
          <ErrorModal
            buttons={[
              {
                theme: 'primary',
                children: formatMessage(messages.carrierSharingValidationErrorBack),
              },
            ]}
            className={styles['error-modal']}
            message={formatMessage(messages.carrierSharingValidationErrorMessage)}
            modalFrameworkMethods={modalFrameworkMethods}
            state='warning'
            title={formatMessage(messages.carrierSharingValidationErrorTitle)}
          />
        </ModalPortal>
      )}
      {showWalletValidationWarning && <WalletWarningModal onCloseWalletValidationWarning={onCloseWalletValidationWarning} />}
      <ModalPortal onClose={onClose}>
        <Modal size='medium'>
          <ModalHeader>
            <FormattedMessage
              {...messages.carrierSharingConfigurationModalHeader}
              values={{
                name,
              }}
            />
          </ModalHeader>
          <ModalBody>
            {showSpinner ? (
              <LoadingIndicator className={styles['loading-indicator']} />
            ) : (
              <div className={styles['modal-body']}>
                {dataAvailable && (
                  <RadioCheckList className={styles['radio-list']}>
                    <RadioCheckListItem className={styles['radio-item']} isChecked={isPartnerDefault} onChecked={onCheckedDefault}>
                      <FormattedMessage {...messages.defaultSettings} />
                    </RadioCheckListItem>
                    <RadioCheckListItem className={styles['radio-item']} isChecked={!isPartnerDefault} onChecked={onCheckedDefault}>
                      <FormattedMessage {...messages.customSettings} />
                    </RadioCheckListItem>
                  </RadioCheckList>
                )}
                {currentCarrierSharingDataLoaded && (
                  <ServicesTreeDataWrapper
                    activeServices={activeServices}
                    carriers={carriers}
                    carrierSharingData={currentCarrierSharingData}
                    disabled={isPartnerDefault}
                    labelProviders={labelProviders}
                    setCarrierSharingData={setCurrentCarrierSharingData}
                    setDataAvailable={setDataAvailable}
                  />
                )}
              </div>
            )}
          </ModalBody>
          <ModalFooter className={styles['modal-footer']}>
            <div className={styles['modification-date']}>
              <FormattedMessage {...messages.lastChangedDateModalMessage} values={{ modificationDate }} />
            </div>
            <ModalFooterCancelButton />
            <Button disabled={!saveButtonActive} onClick={onApplyButtonClick} theme='primary'>
              <FormattedMessage {...messages.saveChanges} />
            </Button>
          </ModalFooter>
        </Modal>
      </ModalPortal>
    </>
  );
});

CustomCarrierSharingModal.propTypes = {
  onClose: PropTypes.func.isRequired,
  provider: PropTypes.oneOfType([ProviderPropType, PendingProviderPropType]).isRequired,
  refreshGridData: PropTypes.func.isRequired,
};
